// Fetch wrapper for the admin panel.
//  - every path is relative to /v2 (the API's version prefix)
//  - a 401 triggers ONE token refresh shared by all parallel requests, then the request is repeated once
//  - the server rejecting the refresh (4xx) ends the session; a network failure, 5xx or 429 does not

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "../auth/tokenStore";

const BASE_URL = (import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
const API_PREFIX = "/v2";

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

let onUnauthorized: () => void = () => {
  window.location.href = "/login";
};

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

const NETWORK_MESSAGE = "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.";
const SERVER_MESSAGE = "Server sedang bermasalah. Coba lagi sebentar lagi.";
const SESSION_MESSAGE = "Sesi berakhir. Silakan masuk kembali.";

function fallbackMessage(status: number): string {
  if (status >= 500) return SERVER_MESSAGE;
  if (status === 429) return "Terlalu banyak permintaan. Coba lagi sebentar lagi.";
  return "Permintaan gagal. Coba lagi.";
}

/** API errors are { error: { code, message, details } }; the rate limiters answer { message } instead. */
async function toApiError(response: Response): Promise<ApiClientError> {
  const body = (await response.json().catch(() => null)) as {
    message?: unknown;
    error?: { code?: unknown; message?: unknown; details?: unknown };
  } | null;
  const message = body?.error?.message ?? body?.message;
  return new ApiClientError(
    typeof message === "string" && message ? message : fallbackMessage(response.status),
    response.status,
    typeof body?.error?.code === "string" ? body.error.code : "UNKNOWN",
    body?.error?.details,
  );
}

async function send(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    return await fetch(`${BASE_URL}${API_PREFIX}${path}`, { ...init, headers });
  } catch {
    throw new ApiClientError(NETWORK_MESSAGE, 0, "NETWORK_ERROR");
  }
}

function endSession(): ApiClientError {
  clearTokens();
  onUnauthorized();
  return new ApiClientError(SESSION_MESSAGE, 401, "SESSION_EXPIRED");
}

// ─── Refresh (one in flight at a time) ────────────────────────

let refreshing: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw endSession();

  const response = await send("/auth/refresh", { method: "POST", body: JSON.stringify({ refreshToken }) }, null);
  if (response.ok) {
    const body = (await response.json().catch(() => null)) as {
      data?: { token?: string; refreshToken?: string };
    } | null;
    const token = body?.data?.token;
    const rotated = body?.data?.refreshToken;
    if (!token || !rotated) throw new ApiClientError(SERVER_MESSAGE, 502, "BAD_REFRESH_RESPONSE");
    setTokens({ accessToken: token, refreshToken: rotated });
    return token;
  }
  // The server looked at the token and refused it: the session is over.
  // 408/429 say nothing about the token, and 5xx means the server could not decide: keep the session.
  const rejected = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429;
  if (rejected) throw endSession();
  throw await toApiError(response);
}

/** Exchanges the stored refresh token for a new pair. Parallel callers share one request. */
export function refreshAccessToken(): Promise<string> {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

// ─── Requests ─────────────────────────────────────────────────

interface RequestOptions {
  /** false for login/refresh-style calls: a 401 is the answer, not a reason to refresh. */
  auth?: boolean;
}

async function request<T>(path: string, init: RequestInit, { auth = true }: RequestOptions = {}): Promise<T> {
  const sentWith = auth ? getAccessToken() : null;
  let response = await send(path, init, sentWith);

  if (response.status === 401 && auth) {
    const current = getAccessToken();
    // Another request already refreshed while this one was in flight: just repeat with the new token.
    const token = current && current !== sentWith ? current : await refreshAccessToken();
    response = await send(path, init, token);
    if (response.status === 401) throw endSession();
  }

  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

const jsonBody = (body: unknown) => (body === undefined ? undefined : JSON.stringify(body));

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { method: "GET" }, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { method: "POST", body: jsonBody(body) }, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { method: "PUT", body: jsonBody(body) }, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { method: "DELETE" }, options),
};
